import apiClient from './client'
import type { Group, CreateGroupRequest, GroupSchedule, UpdateGroupRequest } from '@shared/types/api'

/** Одно занятие, которое изменится при сохранении расписания. */
export interface ScheduleChangeItemDTO {
  date: string
  change: 'added' | 'removed' | 'moved' | 'changed'
  old_start?: string
  old_end?: string
  old_location?: string
  new_start?: string
  new_end?: string
  new_location?: string
  session_id?: string
  session_cancelled?: boolean
  marks: number
  outcome: 'none' | 'kept' | 'deleted'
  new_session_cancelled?: boolean
  later?: boolean
}

/** Версия расписания группы: с даты valid_from действует набор слотов. */
export interface ScheduleVersionDTO {
  /** null - у группы ещё нет версий, это просто groups.schedule */
  id: string | null
  /** null - «с начала» */
  valid_from: string | null
  schedule: GroupSchedule[] | null
}

/** GET /groups/{id}/schedule?today= - текущая и запланированные версии. */
export interface ScheduleTimelineDTO {
  today: string
  default_from: string
  current: ScheduleVersionDTO
  planned: ScheduleVersionDTO[] | null
}

/** POST /groups/{id}/schedule/preview - последствия правки, ничего не меняет. */
export interface SchedulePreviewDTO {
  from: string
  to: string
  changed: boolean
  items: ScheduleChangeItemDTO[] | null
  delete_session_ids: string[] | null
  kept_marked: number
  /** Версия, действующая в день from: от неё «Было», уходит как expected_schedule. */
  base?: ScheduleVersionDTO | null
  /** Запланированные версии с датой >= from: сохранение их заменит. */
  replaced?: ScheduleVersionDTO[] | null
  replace_version_ids?: string[] | null
  /** Только при kept_marked > 0: с этой даты отмеченные занятия не задеваются. */
  suggested_from?: string | null
}

export interface ScheduleReplaceRequest {
  schedule: GroupSchedule[]
  /** Расписание, от которого начиналась правка: другое на сервере - 409 schedule_changed. */
  expected_schedule: GroupSchedule[]
  from: string
  /** Занятия из предпросмотра: другой список на сервере - 409 preview_stale. */
  delete_session_ids: string[]
  /** Запланированные версии из предпросмотра: другой набор - 409 preview_stale. */
  replace_version_ids: string[]
}

export interface ScheduleReplaceResult {
  group: Group
  replayed: boolean
  deleted: number
  kept_marked: number
}

export const groupsApi = {
  // POST /api/v1/assistants/{assistantId}/groups - создать группу
  create: async (assistantId: string, data: CreateGroupRequest): Promise<Group> => {
    const response = await apiClient.post<Group>(`/assistants/${assistantId}/groups`, data)
    return response.data
  },

  // GET /api/v1/groups/{id} - получить группу
  getById: async (id: string): Promise<Group> => {
    const response = await apiClient.get<Group>(`/groups/${id}`)
    return response.data
  },

  // GET /api/v1/assistants/{assistantId}/groups - список групп ассистента
  getByAssistantId: async (assistantId: string): Promise<Group[]> => {
    const response = await apiClient.get<Group[]>(`/assistants/${assistantId}/groups`)
    return response.data
  },

  // PUT /api/v1/groups/{id} - обновить группу
  update: async (id: string, data: UpdateGroupRequest): Promise<Group> => {
    const response = await apiClient.put<Group>(`/groups/${id}`, data)
    return response.data
  },

  // GET /api/v1/groups/{id}/schedule?today= - текущая и запланированные версии расписания
  getScheduleTimeline: async (id: string, today: string): Promise<ScheduleTimelineDTO> => {
    const response = await apiClient.get<ScheduleTimelineDTO>(`/groups/${id}/schedule`, { params: { today } })
    return response.data
  },

  // POST /api/v1/groups/{id}/schedule/preview - что изменится при сохранении расписания
  previewSchedule: async (id: string, schedule: GroupSchedule[], from: string): Promise<SchedulePreviewDTO> => {
    const response = await apiClient.post<SchedulePreviewDTO>(`/groups/${id}/schedule/preview`, { schedule, from })
    return response.data
  },

  // PUT /api/v1/groups/{id}/schedule - сохранить расписание после предпросмотра
  replaceSchedule: async (id: string, data: ScheduleReplaceRequest): Promise<ScheduleReplaceResult> => {
    const response = await apiClient.put<ScheduleReplaceResult>(`/groups/${id}/schedule`, data)
    return response.data
  },

  // GET /api/v1/assistants/{assistantId}/groups/search - поиск групп
  search: async (assistantId: string, query: string): Promise<Group[]> => {
    const response = await apiClient.get<Group[]>(
      `/assistants/${assistantId}/groups/search`,
      { params: { q: query } }
    )
    return response.data
  },

  // POST /api/v1/groups/{id}/athletes/{athleteId} - добавить спортсмена в группу
  addAthlete: async (groupId: string, athleteId: string): Promise<void> => {
    await apiClient.post(`/groups/${groupId}/athletes/${athleteId}`)
  },

  // DELETE /api/v1/groups/{id}/athletes/{athleteId} - удалить спортсмена из группы
  removeAthlete: async (groupId: string, athleteId: string): Promise<void> => {
    await apiClient.delete(`/groups/${groupId}/athletes/${athleteId}`)
  },

  // DELETE /api/v1/groups/{id} - удалить группу
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/groups/${id}`)
  },

  // PUT /api/v1/assistants/{assistantId}/groups/display-order - обновить порядок
  updateDisplayOrders: async (assistantId: string, orders: { group_id: string; display_order: number }[]): Promise<void> => {
    await apiClient.put(`/assistants/${assistantId}/groups/display-order`, {
      group_orders: orders,
    })
  },
}
